import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, ArrowLeft, Maximize } from 'lucide-react';
import './CandidateDetails.css'; // The CSS file name remains as per the original import.
import { getCandidateDetailsById } from '../../../services/admin/api';
import PdfPreviewModal from '../../ClientModule/PdfPreviewModal';
import { fetchCandidateInfo } from '../../../services/user/api';
import { getS3key } from '../../../services/employee/api';

// Helper component for displaying individual information fields
const InfoField = ({ label, value, isLastInGrid = false, isFileField = false }) => (
  <div className={`admin-info-field ${isLastInGrid ? 'admin-last-field-in-grid' : ''} ${isFileField ? 'admin-file-preview-field' : ''}`}>
    <span className="admin-label">{label}</span>
    <span className={`admin-value ${!value ? 'empty' : ''}`}>
      {value || 'Not Provided'}
    </span>
  </div>
);

// Helper component for a section with a title and a grid of children
const DetailSection = ({ title, children, hasData }) => (
  <div className="admin-detail-section">
    <h2 className="admin-section-title">{title}</h2>
    {hasData ? (
      <div className="admin-section-grid">{children}</div>
    ) : (
      <div className="admin-no-data-message">No {title.toLowerCase()} available.</div>
    )}
  </div>
);

const CandidateDetails = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  // Function to handle viewing/downloading any file URL (opens in new tab)
  /*const handleViewFile = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } 
  };*/

  // Function to view full-size image (identical to handleViewFile)
  /*const handleViewFullImage = (imageUrl) => {
    if (imageUrl) {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    } 
  };*/

  const handlePreview = async (fileKey) => {
  if (!fileKey) return;

  try {
    let file = fileKey.split(".com/")[1];
    file = decodeURIComponent(file);

    const response = await getS3key(file);

    const isPdf = file.toLowerCase().endsWith(".pdf");

    const blob = new Blob([response.data], {
      type: isPdf ? "application/pdf" : "image/jpeg"
    });

    const blobUrl = URL.createObjectURL(blob);

    setPreviewUrl(blobUrl);
    setPreviewType(isPdf ? "pdf" : "image");

  } catch (error) {
    console.error("Preview failed:", error);
  }
};


  // Function to navigate back
  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!candidateId) {
        setError('Candidate ID is missing from the URL.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchCandidateInfo(candidateId);
        setData(response.data.data);
      } catch (err) {
     
        setError('No data found. Please try again.'+err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [candidateId]);

  if (loading) {
    return (
      <div className="admin-candidate-wrapper">
        <div className="centered-message">
    <div className="spinner"></div>
    <div>Loading Profile Details ...</div>
  </div>;
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-candidate-wrapper">
        <div className="admin-candidate-container" style={{ textAlign: 'center', padding: '50px', color: '#dc3545' }}>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="admin-back-btn-refresh">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="admin-candidate-wrapper">
        <div className="admin-candidate-container" style={{ textAlign: 'center', padding: '50px' }}>
          <p>No candidate data found.</p>
          <div className="admin-back-btn">
            <button onClick={handleBack}>
              <ArrowLeft size={18} /> Back to Candidate List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-candidate-wrapper">
      <div className="admin-candidate-container">
        <h1 className="admin-main-heading">Review Candidate Details</h1>

        {/* Personal Details Section */}
        <DetailSection title="Personal Details" hasData={!!data.personalInfoResponse}>
          {data.personalInfoResponse && (
            <>
              <InfoField label="First Name" value={data.personalInfoResponse.firstName} />
              <InfoField label="Last Name" value={data.personalInfoResponse.lastName} />
              <InfoField label="Father Name" value={data.personalInfoResponse.fatherName} />
              <InfoField label="Date Of Birth" value={data.personalInfoResponse.dateOfBirth} />
              <InfoField label="Email" value={data.personalInfoResponse.email} />
              <InfoField label="Gender" value={data.personalInfoResponse.gender} />
              <InfoField label="Mobile Number" value={data.personalInfoResponse.mobileNumber} />
              <InfoField label="Alternate Mobile Number" value={data.personalInfoResponse.alternateMobileNumber} />
              <InfoField label="Aadhaar Card Number" value={data.personalInfoResponse.aadhaarCardNumber} />
              {data.personalInfoResponse.personalImageFileName && (
                <div className="admin-image-preview">
                  <button
                    className="admin-view-full-image-btn"
                    onClick={() => handlePreview(data.personalInfoResponse.personalImageFileName)}
                  >
                    <Maximize size={18} /> View Full Image
                  </button>
                </div>
              )}
            </>
          )}
        </DetailSection>

        {/* Current Address Section */}
        <DetailSection title="Current Address" hasData={!!data.addressInfoResponse}>
          {data.addressInfoResponse && (
            <>
              <InfoField label="Address Line 1" value={data.addressInfoResponse.currentAddressLine1} />
              <InfoField label="Address Line 2" value={data.addressInfoResponse.currentAddressLine2} />
              <InfoField label="Country" value={data.addressInfoResponse.currentCountry} />
              <InfoField label="State" value={data.addressInfoResponse.currentState} />
              <InfoField label="City" value={data.addressInfoResponse.currentCity} />
              <InfoField label="Pin Code" value={data.addressInfoResponse.currentPinCode} />
              {data.addressInfoResponse.currentAddressFile && (
                <InfoField
                  label="Address Proof"
                  value={
                    <div className="admin-file-preview-content">
                      {data.addressInfoResponse.currentAddressFile.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                        <img src={data.addressInfoResponse.currentAddressFile} alt="Address Proof Preview" />
                      ) : (
                        <div className="admin-file-placeholder">📄</div>
                      )}
                      <button className="admin-view-full-image-btn" onClick={() => handlePreview(data.addressInfoResponse.currentAddressFile)}>
                        <Maximize size={18} /> Preview Current Address Proof
                      </button>
                    </div>
                  }
                  isLastInGrid={true}
                  isFileField={true}
                />
              )}
            </>
          )}
        </DetailSection>

        {/* Permanent Address Section */}
        <DetailSection title="Permanent Address" hasData={!!data.addressInfoResponse}>
          {data.addressInfoResponse && (
            <>
              <InfoField label="Address Line 1" value={data.addressInfoResponse.permanentAddressLine1} />
              <InfoField label="Address Line 2" value={data.addressInfoResponse.permanentAddressLine2} />
              <InfoField label="Country" value={data.addressInfoResponse.permanentCountry} />
              <InfoField label="State" value={data.addressInfoResponse.permanentState} />
              <InfoField label="City" value={data.addressInfoResponse.permanentCity} />
              <InfoField label="Pin Code" value={data.addressInfoResponse.permanentPinCode} />
              {data.addressInfoResponse.permanentAddressFile && (
                <InfoField
                  label="Address Proof"
                  value={
                    <div className="admin-file-preview-content">
                      {data.addressInfoResponse.permanentAddressFile.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                        <img src={data.addressInfoResponse.permanentAddressFile} alt="Permanent Address Proof Preview" />
                      ) : (
                        <div className="admin-file-placeholder">📄</div>
                      )}
                      <button className="admin-view-full-image-btn" onClick={() => handlePreview(data.addressInfoResponse.permanentAddressFile)}>
                        <Maximize size={18} /> Preview Permanent Address Proof
                      </button>
                    </div>
                  }
                  isLastInGrid={true}
                  isFileField={true}
                />
              )}
            </>
          )}
        </DetailSection>

        {/* Education Details Section */}
        <DetailSection title="Education Details" hasData={data.educationInfos && data.educationInfos.length > 0}>
          {data.educationInfos?.length > 0 ? (
            data.educationInfos.map((edu, index) => (
              <React.Fragment key={index}>
                <InfoField label="Education Level" value={edu.educationLevel} />
                <InfoField label="Institute" value={edu.instituteName} />
                <InfoField label="Qualification" value={edu.qualification} />
                <InfoField label="Specialization" value={edu.specialization} />
                <InfoField label="Enrolled Year" value={edu.enrolledYear} />
                <InfoField label="Passing Year" value={edu.passingYear} />
                <InfoField label="Address" value={edu.address} />
                <InfoField label="City" value={edu.city} />
                <InfoField label="State" value={edu.stateRegionProvince} />
                <InfoField label="Country" value={edu.country} />
                <InfoField label="Zip Code" value={edu.zipCode} />
                {edu.certificateFileUrl && (
                  <InfoField
                    label="Certificate"
                    value={
                      <div className="admin-file-preview-content">
                        {edu.certificateFileUrl.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                          <img src={edu.certificateFileUrl} alt="Certificate Preview" />
                        ) : (
                          <div className="admin-file-placeholder">📄</div>
                        )}
                        <button className="admin-view-full-image-btn" onClick={() => handlePreview(edu.certificateFileUrl)}>
                          <Maximize size={18} /> Preview Certificate
                        </button>

                      </div>
                    }
                    isLastInGrid={true}
                    isFileField={true}
                  />
                )}
              </React.Fragment>
            ))
          ) : null}
        </DetailSection>

        {/* Employment Details Section */}
        <DetailSection title="Employment Details" hasData={data.employmentInfos && data.employmentInfos.length > 0}>
          {data.employmentInfos?.length > 0 ? (
            data.employmentInfos.map((emp, index) => (
              <React.Fragment key={index}>
                <InfoField label="Company Name" value={emp.companyName} />
                <InfoField label="Employee Code" value={emp.employeeCode} />
                <InfoField label="Job Title" value={emp.jobTitle} />
                <InfoField label="Location" value={emp.location} />
                <InfoField label="Working From" value={emp.workingFrom} />
                <InfoField label="Working To" value={emp.workingTo} />
                <InfoField label="HR Contact Email" value={emp.hrContactEmail} />
                <InfoField label="HR Contact Number" value={emp.hrContactNumber} />
                {(emp.offerLetterUrl || emp.salarySlipsUrl || emp.bankStatementUrl) && (
                  <InfoField
                    label="Employment Documents"
                    value={
                      <div className="admin-multiple-files-container">
                        {emp.offerLetterUrl && (
                          <div className="admin-file-preview-content">
                            {emp.offerLetterUrl.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                              <img src={emp.offerLetterUrl} alt="Offer Letter Preview" />
                            ) : (
                              <div className="admin-file-placeholder">📄</div>
                            )}
                            <button className="admin-view-full-image-btn" onClick={() => handlePreview(emp.offerLetterUrl)}>
                              <Maximize size={18} /> Preview Offer Letter
                            </button>
                          </div>
                        )}
                        {emp.salarySlipsUrl &&
                          emp.salarySlipsUrl.split(',').map((url, idx) => (
                            <div key={idx} className="admin-file-preview-content">
                              {url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                                <img src={url} alt={`Salary Slip ${idx + 1} Preview`} />
                              ) : (
                                <div className="admin-file-placeholder">📄</div>
                              )}
                              <button className="admin-view-full-image-btn" onClick={() => handlePreview(url)}>
                                <Maximize size={18} /> Preview Salary Slip {idx + 1}
                              </button>
                            </div>
                          ))}
                        {emp.bankStatementUrl && (
                          <div className="admin-file-preview-content">
                            {emp.bankStatementUrl.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                              <img src={emp.bankStatementUrl} alt="Bank Statement Preview" />
                            ) : (
                              <div className="admin-file-placeholder">📄</div>
                            )}
                            <button className="admin-view-full-image-btn" onClick={() => handlePreview(emp.bankStatementUrl)}>
                              <Maximize size={18} /> Preview Bank Statement
                            </button>
                          </div>
                        )}
                      </div>
                    }
                    isLastInGrid={true}
                    isFileField={true}
                  />
                )}
              </React.Fragment>
            ))
          ) : null}
        </DetailSection>

        <div className="admin-back-btn">
          <button onClick={handleBack}>
            <ArrowLeft size={18} /> Back to Candidate List
          </button>
        </div>
      </div>
     {previewUrl && (
  <PdfPreviewModal
    fileUrl={previewUrl}
    fileType={previewType}
    onClose={() => setPreviewUrl(null)}
  />
)}

    </div>
  );
};

export default CandidateDetails;
